import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import PageLayout from '@/components/layout/PageLayout'
import ChatInterface from '@/components/ChatInterface'
import { useEffect, useRef, useState, useCallback } from 'react'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { ChatV2 } from '@/features/chatV2'
import { Loader2 } from 'lucide-react'

const AIChat = () => {
  const { chat_id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setContextId, setInputValue, setCurrentConversationId, setChatMessages, boardList, loadBoardList, createBoard, generateDefaultBoardName, user } = useAppStore()
  const { initialMessage, autoSend } = location.state || {}
  const lastResetKeyRef = useRef<string | null>(null)
  const [isLoadingBoards, setIsLoadingBoards] = useState(false)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const hasAttemptedAutoSelectRef = useRef(false)

  // Feature flag for Vercel AI SDK chat (build-time env var only)
  const useVercelAIChat = useFeatureFlag('VITE_USE_VERCEL_AI_CHAT')

  // Load board list when component mounts if not already loaded
  // Runs for both ChatV2 (needs board selector) and when no chat_id
  useEffect(() => {
    const loadBoards = async () => {
      // Load boards for ChatV2 or when no chat_id selected
      const needsBoards = useVercelAIChat || !chat_id
      if (needsBoards && !hasLoadedOnce) {
        setIsLoadingBoards(true)
        setHasLoadedOnce(true)
        await loadBoardList()
        setIsLoadingBoards(false)
      }
    }
    loadBoards()
  }, [chat_id, hasLoadedOnce, loadBoardList, useVercelAIChat])

  // Handle board auto-selection or creation
  const handleBoardSelection = useCallback(async () => {
    if (chat_id) {
      setContextId(chat_id)
      hasAttemptedAutoSelectRef.current = false // Reset the flag when chat_id changes
      return
    }

    // Don't proceed if still loading or already attempted
    if (isLoadingBoards || hasAttemptedAutoSelectRef.current) {
      return
    }

    // Wait for boards to load before making decisions
    if (!hasLoadedOnce) {
      return
    }

    // Set flag to prevent multiple auto-selections
    hasAttemptedAutoSelectRef.current = true

    const chatPathPrefix = '/chat/'

    if (boardList.length > 0) {
      // If boards exist, select the most recent one
      // Sort by updated_at descending to get the most recently updated board
      // Handle null/undefined updated_at values safely
      const sortedBoards = [...boardList].sort((a, b) => {
        const timeB = b.updated_at ? new Date(b.updated_at).getTime() : 0
        const timeA = a.updated_at ? new Date(a.updated_at).getTime() : 0
        return timeB - timeA
      })
      const mostRecentBoard = sortedBoards[0]

      if (mostRecentBoard) {
        // Navigate to the most recent board instead of just setting context
        // This ensures the URL reflects the selected board
        navigate(`${chatPathPrefix}${mostRecentBoard.id}`, {
          replace: true,
          state: { initialMessage, autoSend }
        })
      } else {
        // Fall back to most recent board if no Master Board exists
        const sortedBoards = [...boardList].sort((a, b) => {
          const timeB = b.updated_at ? new Date(b.updated_at).getTime() : 0
          const timeA = a.updated_at ? new Date(a.updated_at).getTime() : 0
          return timeB - timeA
        })
        const mostRecentBoard = sortedBoards[0]

        if (mostRecentBoard) {
          navigate(`${chatPathPrefix}${mostRecentBoard.id}`, {
            replace: true,
            state: { initialMessage, autoSend }
          })
        }
      }
    } else if (isAnnotatorRole(user?.role)) {
      // Annotators can't create boards (read-only) — don't fire the blocked
      // POST /v1/workspaces on mount. Send them to their P&ID catalog instead.
      navigate('/data-catalog', { replace: true })
    } else {
      // No boards exist — create the user's first board on demand (FS-553
      // removed the auto-created Master Board; we make a normal, named board).
      const newBoard = await createBoard(generateDefaultBoardName())
      if (newBoard) {
        navigate(`${chatPathPrefix}${newBoard.id}`, {
          replace: true,
          state: { initialMessage, autoSend }
        })
      } else {
        // If board creation failed, reset flag to allow retry
        hasAttemptedAutoSelectRef.current = false
      }
    }
  }, [chat_id, isLoadingBoards, hasLoadedOnce, boardList, setContextId, navigate, initialMessage, autoSend, createBoard, generateDefaultBoardName, user])

  // Run board selection logic when dependencies change
  useEffect(() => {
    handleBoardSelection()
  }, [handleBoardSelection])

  // Handle conversation reset and initial message (only for v1)
  useEffect(() => {
    if (useVercelAIChat) return // Skip for v2

    // Reset conversation state when starting a new chat with autoSend
    // Use location.key which is unique per navigation (React Router generates it)
    const navigationKey = location.key || 'default'
    if (autoSend && navigationKey !== lastResetKeyRef.current) {
      setCurrentConversationId(null)
      setChatMessages([])
      lastResetKeyRef.current = navigationKey
    }

    if (initialMessage) {
      setInputValue(initialMessage)
    }
  }, [location.key, initialMessage, autoSend, setInputValue, setCurrentConversationId, setChatMessages, useVercelAIChat])

  // Handler for board change in ChatV2
  const handleBoardChange = useCallback((boardId: string) => {
    navigate(`/chat/${boardId}`, { replace: true })
  }, [navigate])

  /**
   * FS-663: `?c=<conversationId>` from a shared chat link.
   *
   * Read HERE, in one place, rather than inside either chat surface: the board
   * canvas renders the LEGACY ChatInterface while this route can render ChatV2,
   * and the two use different Zustand stores. A handler written for one would
   * silently no-op on the other.
   */
  const sharedConversationId = searchParams.get('c')
  const appliedSharedConversationRef = useRef<string | null>(null)

  useEffect(() => {
    if (!sharedConversationId || !chat_id) return
    // One-shot: re-applying on every render would fight the user navigating to
    // a different conversation in the same board.
    if (appliedSharedConversationRef.current === sharedConversationId) return
    appliedSharedConversationRef.current = sharedConversationId

    if (!useVercelAIChat) {
      // `expectedContextId` makes the store reject a `?c=` pointing at another
      // board's conversation rather than loading it into the wrong context.
      void useAppStore.getState().selectConversation(sharedConversationId, chat_id)
    }

    // Drop the param once consumed so a later in-app navigation or refresh does
    // not re-select it over whatever the user has since opened.
    const next = new URLSearchParams(searchParams)
    next.delete('c')
    navigate(
      { pathname: `/chat/${chat_id}`, search: next.toString() ? `?${next.toString()}` : '' },
      { replace: true, state: location.state },
    )
  }, [sharedConversationId, chat_id, useVercelAIChat, searchParams, navigate, location.state])

  // Show loading state while initializing (flag loading or boards loading without chat_id)
  const isInitializing = !chat_id && isLoadingBoards

  if (isInitializing) {
    return (
      <PageLayout fullScreenContent>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      </PageLayout>
    )
  }

  // Render ChatV2 if feature flag is enabled
  if (useVercelAIChat && chat_id) {
    return (
      <PageLayout fullScreenContent>
        <ChatV2
          contextId={chat_id}
          {...(sharedConversationId ? { initialConversationId: sharedConversationId } : {})}
          contextType="workspace"
          boardList={boardList}
          onBoardChange={handleBoardChange}
          autoSendMessage={autoSend || false}
          navigationKey={location.key}
          initialMessage={initialMessage}
        />
      </PageLayout>
    )
  }

  // Default: Render existing ChatInterface
  return (
    <PageLayout fullScreenContent>
      <ChatInterface
        context_type="workspace"
        chatMode="global"
        autoSendMessage={autoSend || false}
        navigationKey={location.key}
        initialMessage={initialMessage}
      />
    </PageLayout>
  )
}

export default AIChat
